import type { CSSProperties } from 'react';
import type { MarketingStrategy } from '@/types';

/** Charcoal chart palette — warm neutral steps, no accent rainbow. */
export const STRATEGY_CHART_COLORS = [
  '#1c1a18',
  '#3a3632',
  '#57524c',
  '#6e6862',
  '#a19b94',
  '#d5cfc8',
] as const;

export const strategyChartTooltipProps = {
  contentStyle: {
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'var(--card)',
    color: 'var(--foreground)',
  } as CSSProperties,
};

export function truncateChartLabel(s: string, max = 14): string {
  const t = s.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/**
 * Heuristic 0–100 scores from strategy JSON (structural overview, not market research).
 */
export function buildMarketProfileRadarData(strategy: MarketingStrategy): { axis: string; value: number }[] {
  const seg = strategy.target_market?.segments?.length ?? 0;
  const per = strategy.target_market?.personas?.length ?? 0;
  const geo = strategy.target_market?.geography?.length ?? 0;
  const ch = strategy.channel_strategy?.channels?.length ?? 0;
  const comp = strategy.competitive_landscape?.competitors?.length ?? 0;
  const str = strategy.competitive_landscape?.strengths?.length ?? 0;

  const clamp100 = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

  return [
    { axis: 'Segmentierung', value: clamp100(seg * 25) },
    { axis: 'Personas', value: clamp100(per * 34) },
    { axis: 'Geo', value: clamp100(geo * 34) },
    { axis: 'Kanäle', value: clamp100(ch * 20) },
    { axis: 'Wettbewerb', value: clamp100(comp * 15) },
    { axis: 'Stärken', value: clamp100(str * 20) },
  ];
}

export function hasAnyMarketProfileSignal(strategy: MarketingStrategy): boolean {
  const d = buildMarketProfileRadarData(strategy);
  return d.some((x) => x.value > 0);
}

const ACQUISITION_RE = /lead|akquisition|neukund|gewinnung|conversion|kunde|pipeline|mql|sql/i;

export function filterAcquisitionKpis<T extends { name: string }>(kpis: T[]): T[] {
  const hit = kpis.filter((k) => ACQUISITION_RE.test(k.name));
  return hit.length > 0 ? hit : kpis;
}
